<script setup lang="ts">
// ================================
// LE DOSSIER DE L'ÉTUDE, RÉSERVÉ À L'ÉQUIPE (DEV-2311, décision du 05/10/2026).
//
// Ce que le client doit vivre : rien. Avant, la page lui montrait « Faire relire
// cette étude », qui dépliait un dossier technique (identifiant de l'étude,
// code de la cause, versions des composants) à copier et à nous envoyer. Le
// directeur général l'a retiré de l'écran client : ces détails n'y ont pas leur
// place.
//
// Ce que l'équipe garde : depuis une session ouverte au mot de passe maître, le
// même dossier, replié, pour retrouver le cas sans aller-retour. Le composant ne
// rend RIEN hors d'une session de l'équipe (`estSessionDeLEquipe`) : le
// monter partout est donc sans risque pour un client.
// ================================
import { computed, ref } from "vue"
import { useI18n } from "vue-i18n"
import { construireDossierSupport } from "@modules/courtage/utils/optim-turpe-v2.support"
import { estSessionDeLEquipe } from "@modules/courtage/utils/optim-turpe-v2.equipe"
import type { OptimTurpeV2Etude } from "@modules/courtage/types/optim-turpe-v2"

const { t } = useI18n({
    locale: "fr",
    messages: {
        fr: {
            titre: "Dossier de l'étude, réservé à l'équipe",
            intro: "Visible seulement depuis une session de l'équipe, jamais par le client.",
            copier: "Copier le dossier",
            copie: "Dossier copié.",
            copieImpossible:
                "La copie automatique n'a pas fonctionné : sélectionnez le texte ci-dessous.",
        },
    },
})

type Props = { etude: OptimTurpeV2Etude }
const props = defineProps<Props>()

/** Lu au rendu, comme l'interrupteur : fermé par défaut. */
const getEquipe = computed(() => estSessionDeLEquipe())

const open = ref<boolean>(false)
const copie = ref<"" | "ok" | "echec">("")

const getDossier = computed(() => construireDossierSupport(props.etude))

/**
 * La copie peut échouer sans exception (contexte non sécurisé, permission
 * refusée) : on ne prétend pas avoir copié. Le texte reste sélectionnable.
 */
async function onCopier(): Promise<void> {
    try {
        await navigator.clipboard.writeText(getDossier.value)
        copie.value = "ok"
    } catch {
        copie.value = "echec"
    }
}
</script>

<template>
    <div
        v-if="getEquipe"
        class="optim-turpe-v2-dossier-equipe rounded-lg border border-dashed border-surface-300 bg-surface-50"
        data-testid="optim-turpe-v2-dossier-equipe"
    >
        <button
            type="button"
            class="flex w-full items-center justify-between px-5 py-3 text-left"
            :aria-expanded="open"
            @click="open = !open"
        >
            <span class="text-sm font-medium text-primary-700">{{
                t("titre")
            }}</span>
            <BaseIcon
                :icon="open ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"
                size="0.8rem"
                class="text-primary-400"
                aria-hidden="true"
            />
        </button>

        <div v-if="open" class="flex flex-col gap-3 px-5 pb-5">
            <p class="text-xs text-primary-500">{{ t("intro") }}</p>
            <pre
                class="max-h-64 overflow-auto whitespace-pre-wrap rounded-md bg-white p-3 text-xs text-primary-800"
                >{{ getDossier }}</pre
            >
            <div class="flex flex-wrap items-center gap-2">
                <BaseButton
                    :label="t('copier')"
                    icon="pi pi-copy"
                    size="small"
                    outlined
                    severity="secondary"
                    @click="onCopier"
                />
                <span v-if="copie === 'ok'" class="text-xs text-primary-500">{{
                    t("copie")
                }}</span>
                <span
                    v-else-if="copie === 'echec'"
                    class="text-xs text-[var(--p-orange-700)]"
                    >{{ t("copieImpossible") }}</span
                >
            </div>
        </div>
    </div>
</template>
